import axios from 'app/client';
import {
	arrayify,
	getMetadataUrl,
	getNodeTestingAPI,
	groupByID,
	getApproveNodesUrl,
	getCollabServiceUrl,
} from 'app/utils/helpers';
import { getProfile, getTrackedJobs, getSelectedLicenseGroupId } from 'app/store/reducers';
import { type AppThunk } from 'app/store';
import _ from 'lodash';
import * as appActions from './app.actions';
import {
	type InboxItem,
	type JobItemDetails,
	type JobNote,
	type JobQueue,
	type Profile,
	type User,
	type UserGroup,
	type JobHistory,
	type CollaboratorPermissions,
	type ExternalCollabPermissions,
	type CollabJobItem,
} from '../types';

const findErrors = (res: any) => {
	const err: any = [];
	arrayify(res).forEach((response: any) => {
		if (Array.isArray(response.data)) {
			response.data.forEach((datum: any) => {
				if (datum.type) {
					err.push(datum.type);
				}
			});
		}
	});
	return err;
};

const massageRawJob = (rawJob: any) => {
	const job: JobItemDetails = {
		...rawJob,
		attachments:
			rawJob.attachments?.map((attachment: any) => ({
				...attachment,
				metadata: {
					...attachment.metadata,
					// HACK::some extension are missing a prefixed `.` - the site now assumes none of them have one
					...(attachment.metadata.ext ? { ext: attachment.metadata.ext.replace(/^\./, '') } : {}),
				},
				fileId: attachment.url.split('?')[0].split('/').pop(),
			})) ?? [],
	};

	return job;
};

export const getUserJobs =
	(onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const response = await axios.get(`${jobsApiUrl}/people/jobs/user`, {
				params: {
					userID: profile.id,
				},
			});
			dispatch({
				type: 'GET_USER_JOBS_SUCCESS',
				payload: groupByID(response.data.jobs),
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'jobs:get:fail'));
			onError();
		}
	};

export const getUserCollabJobs =
	(jobId: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		try {
			const response = await axios.get(`${collabServUrl}/api/jobs/get/${profile.tenantId}/${jobId}`, {
				validateStatus: (stratus) => stratus < 400,
			});
			const internalUserPermissions = response.data.users.findIndex((userId: string) => userId === profile.id);
			const externalUserPermissions = response.data.guests.findIndex((userId: string) => userId === profile.id);
			let permissions: { [key: string]: boolean } = {};
			if (internalUserPermissions !== -1) {
				permissions = response.data.permissions.internalCollabPermissions;
			}
			if (externalUserPermissions !== -1) {
				permissions = response.data.permissions.externalCollabPermissions;
			}

			dispatch({
				type: 'GET_COLLAB_JOB_RECORD_SUCCESS',
				payload: {
					...response.data,
					jobPermissions: permissions,
				},
			});
			onSuccess();
		} catch (error) {
			dispatch({
				type: 'GET_COLLAB_JOB_RECORD_SUCCESS',
				payload: {},
			});
			onError();
		}
	};

export const resetUserCollabJobs =
	(jobId: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		dispatch({
			type: 'RESET_COLLAB_JOB_RECORD_SUCCESS',
			payload: {},
		});
	};

export const getSelectedJob =
	(jobID: string, onSuccess = () => {}, onError?: Function): AppThunk =>
	async (dispatch, getState) => {
		console.log('getting job', jobID);
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const { data } = await axios.get(`${jobsApiUrl}/people/job`, {
				params: {
					jobID,
				},
			});
			console.log('selectedJob', data.job);
			console.log('selectedJob - massaged', massageRawJob(data.job));
			dispatch({
				type: 'GET_SELECTED_JOB_SUCCESS',
				payload: massageRawJob(data.job),
			});
			onSuccess();
		} catch (error) {
			console.log(error);
			if (onError) {
				onError();
			} else {
				dispatch(appActions.handleError(error, 'job:get:fail'));
			}
		}
	};

export const fetchJobDropData =
	(jobID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const apiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const processResponse = await axios.get(`${apiUrl}/workflow/process/${jobID}`);
			const dropData = processResponse.data.process.drop;
			dispatch({
				type: 'GET_DROP_DATA_SUCCESS',
				payload: dropData,
			});

			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'dropData:get:fail'));
			onError();
		}
	};

export const resetSelectedJob = (): AppThunk => async (dispatch, getState) => {
	dispatch({
		type: 'RESET_SELECTED_JOB_SUCCESS',
	});
};

export const getUserQueues =
	(onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const response = await axios.get(`${jobsApiUrl}/people/queues/user`, {
				params: {
					userID: profile.id,
				},
			});
			const { data } = response;
			console.log('user queues', data.queues);
			dispatch({
				type: 'GET_USER_QUEUES_SUCCESS',
				payload: groupByID(data.queues),
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'queues:get:fail'));
			onError();
		}
	};
export const getTenantQueues = (): AppThunk => async (dispatch, getState) => {
	const profile = getProfile(getState());
	const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
	try {
		const response = await axios.get(`${jobsApiUrl}/people/queues`);
		const { data } = response;
		console.log('tenant queues', data.queues);
		dispatch({
			type: 'GET_TENANT_QUEUES_SUCCESS',
			payload: groupByID(data.queues),
		});
	} catch (error) {
		dispatch(appActions.handleError(error, 'queues:get:fail'));
	}
};

export const getQueueJobs =
	(queueID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const response = await axios.get(`${jobsApiUrl}/people/jobs/queue`, {
				params: {
					queueID,
				},
			});
			const { data } = response;
			console.log('queue', queueID, 'jobs', data.jobs);
			dispatch({
				type: 'GET_QUEUE_JOBS_SUCCESS',
				payload: {
					id: queueID,
					jobs: groupByID(data.jobs),
				},
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'queue:jobs:get:fail'));
			onError();
		}
	};

export const getJobNotes =
	(jobId: string): AppThunk =>
	async (dispatch, getState) => {};

export const getJobHistory =
	(jobId: string): AppThunk =>
	async (dispatch, getState) => {};

export const removeJob =
	(jobId: string): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.delete(`${jobsApiUrl}/people/job`, {
				params: {
					tenantID: tenantId,
					jobID: jobId,
				},
			});
		} catch (error) {
			dispatch(appActions.alert('jobs:job remove:fail', 'warning'));
		}
		dispatch(appActions.alert('jobs:job remove:success', 'success'));
		dispatch(getUserJobs());
	};

const massageRawInboxItem = (rawInboxItem: any) => {
	// HACK::capture values inside single quotes or at end of string with colon and treat them as variables
	const messageToMessageKey = (message: string) => {
		const pattern = /'([^']*)'|: ([^\s]+)$/g;

		const matches = message.match(pattern) || [];
		let counter = 0;

		const messageKey: InboxItem['messageKey'] = message.replace(pattern, () => `{{${counter++}}}`);

		const info =
			matches.length === 0
				? undefined
				: matches.reduce(
						(acc, match, index) => {
							acc[index] = match;
							return acc;
						},
						{} as InboxItem['info'],
					);

		return {
			messageKey,
			info,
		};
	};

	const { messageKey, info } = messageToMessageKey(rawInboxItem.message || '');

	const inboxItem: InboxItem = {
		id: rawInboxItem.id,
		type: rawInboxItem.type,
		status: rawInboxItem.status,
		job: rawInboxItem.job,
		origin: {
			id: rawInboxItem.origin.id ? rawInboxItem.origin.id : '',
			type: rawInboxItem.origin.type ? rawInboxItem.origin.type : '',
			name: rawInboxItem.origin.name ? rawInboxItem.origin.name : '',
		},
		createdAt: rawInboxItem.createdAt,
		// message: rawInboxItem.message,
		messageKey,
		info: typeof info === 'object' && info !== null ? info : {},
		data: rawInboxItem.data,
	};
	return inboxItem;
};

export const getInbox =
	(onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const response = await axios.get(`${jobsApiUrl}/people/inbox`, {
				params: {
					userID: profile.id,
				},
			});

			const inbox: { [id: string]: InboxItem } = _.mapValues(_.keyBy(response.data.inbox, 'id'), (inboxItem) =>
				massageRawInboxItem(inboxItem),
			);

			dispatch({
				type: 'GET_USER_INBOX_SUCCESS',
				payload: inbox,
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'inbox:get:fail'));
			onError();
		}
	};
export const deleteInboxItem =
	(itemId: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.delete(`${jobsApiUrl}/people/inbox`, {
				params: {
					userID: profile.id,
					item: itemId,
				},
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'inbox:delete:fail'));
			onError();
		}
		dispatch(getInbox());
	};

export const postNote =
	(note: Pick<JobNote, 'note' | 'private'>, jobID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.post(`${jobsApiUrl}/people/job/notes`, {
				jobID,
				userID: profile.id,
				note: note.note,
				private: note.private,
				userName: `${profile.firstName} ${profile.lastName}`.trim(),
			});
			dispatch(getSelectedJob(jobID));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'note:post:fail'));
			onError();
		}
	};

export const removeAttachments =
	(fileIds: string[], job: JobItemDetails, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const metadataApiUrl = getMetadataUrl(profile?.awsRegion);
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const droppoint = job.drop as { id: string; username: string; password: string };
		try {
			const ntfResponses = await Promise.all(
				fileIds.map((fileId) =>
					axios.delete(`${jobsApiUrl}/delete-file`, {
						data: {
							filename: fileId,
							dropid: droppoint.id,
							username: droppoint.username,
							password: droppoint.password,
						},
					}),
				),
			);
			const metadataResponses = await Promise.all(
				fileIds.map((fileId) => axios.delete(`${metadataApiUrl}/api/v1/file/${job.id}/${fileId}`)),
			);

			const fileMetadataReponses = await axios.patch(
				`${metadataApiUrl}/api/v1/process/${job.id}/${job.workflow.id}`,
				{
					files: fileIds,
				},
			);

			const ntfErr: any = findErrors(ntfResponses);
			const metadataErr: any = findErrors(metadataResponses);
			const metadataFileErr: any = findErrors(fileMetadataReponses);
			if (!_.isEmpty(ntfErr)) {
				console.log('Error in removeAttachment:', ntfErr);
				onError();
				return false;
			}
			if (!_.isEmpty(metadataErr)) {
				console.log('Error in removeAttachment:', metadataErr);
				onError();
				return false;
			}
			if (!_.isEmpty(metadataFileErr)) {
				console.log('Error in removeAttachment:', metadataFileErr);
				onError();
				return false;
			}
			if (ntfResponses.every((response) => response.data.result !== 'success')) {
				dispatch(appActions.alert('jobs:file remove:fail', 'warning'));
				onError();
				return false;
			}

			const deletionResponse = await axios.delete(`${jobsApiUrl}/people/job/attachments`, {
				params: {
					jobID: job.id,
					files: fileIds.join(','),
				},
			});
			const JobsErr: any = findErrors(deletionResponse);
			if (!_.isEmpty(JobsErr)) {
				dispatch(appActions.alert('jobs:file remove:fail', 'warning'));
				onError();
			}
			dispatch(getSelectedJob(job.id));
			dispatch(appActions.alert('jobs:file remove:success', 'success'));
			return deletionResponse.data;
		} catch (error) {
			console.error('Error in removeAttachment:', error);
			onError();
			return false;
		}
	};

export const postAttachment =
	(
		files: File[],
		job: JobItemDetails,
		workflow: any,
		setTotalProgress: Function,
		setProgressData: Function,
		onSuccess: Function,
		onError: Function,
		setCollabSessionState: Function,
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const fileResponses = [];
		const stage =
			process.env.REACT_APP_ENVIRONMENT === 'development'
				? 'DEV'
				: process.env.REACT_APP_ENVIRONMENT === 'testing'
					? 'STAGING'
					: 'PROD';

		// const mockmockProgress = (resolve: any, num: number, fileName: string) => {
		// 	setTimeout(() => {
		// 		setProgressData(num * 10, fileName);
		// 		if (num * 10 === 100) {
		// 			resolve();
		// 			return;
		// 		}
		// 		mockmockProgress(resolve, num + 1, fileName);
		// 	}, 250);
		// };

		// const mockProgress = (num: number, fileName: string) => {
		// 	return new Promise<void>((resolve, reject) => {
		// 		mockmockProgress(resolve, num, fileName);
		// 	});
		// };

		const initiateUpload = async (file: File) => {
			try {
				const fileData = {
					arn: job.id,
					tenantId,
					tenantRegion: profile.awsRegion,
					userId: profile.id,
					filename: file.name,
					deviceId: `${tenantId}-stratus-portal`,
					stage,
					drop: job.drop,
				};
				const response = await axios.post(`/api/node/initiateUpload`, fileData);
				const err: any = findErrors(response);
				if (!_.isEmpty(err)) {
					onError(file.name);
					return false;
				}
				return response.data;
			} catch (error) {
				console.error('Error in initiateUpload:', error);
				onError(file.name);
				return false;
			}
		};

		const uploadFileToDroppoint = async (file: File, droppoint: any) => {
			try {
				const formData = new FormData();
				formData.append('file', file);

				// await mockProgress(0, file.name);
				const response = await axios.put(droppoint.info.url, file, {
					transformRequest: [
						(data, headers: any) => {
							console.log(headers);
							if (headers?.common?.Authorization) {
								delete headers.common?.Authorization;
							}

							return data;
						},
					],
					headers: { ...droppoint.info.headers, 'Content-Type': file.type },
					onUploadProgress: (progressEvent) => {
						const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
						setProgressData(percentCompleted, file.name);
					},
				});
				const err: any = findErrors(response);
				if (!_.isEmpty(err)) {
					onError(file.name);
					return false;
				}
				return true;
			} catch (error) {
				setProgressData(0, ''); // Reset progress on error
				console.error('Error in uploadFileToDroppoint:', error);
				console.error('Error uploading to droppoint:', error);
				onError(file.name);
				return false;
			}
		};

		const finalizeUpload = async (file: File, droppoint: any, fileId: string) => {
			const uploadData = {
				arn: job.id,
				filename: file.name,
				originalSize: file.size,
				tenantId,
				tenantRegion: profile.awsRegion,
				userId: profile.id,
				workflowName: workflow.name,
				workflowId: workflow.id,
				deviceId: `${tenantId}-stratus-portal`,
				stage,
				droppoint,
				fileId,
			};
			try {
				const response = await axios.post('/api/node/finalizeUpload', uploadData);
				const err: any = findErrors(response);
				if (!_.isEmpty(err)) {
					onError(file.name);
					return false;
				}
				return response.data;
			} catch (error) {
				console.error('Error in finalizeUpload:', error);
				onError(file.name);
				return false;
			}
		};

		const jobUpdate = async (responses: { id: string; response: { url: string; headers: any } }[]) => {
			try {
				const jobResponse = await axios.post(`${jobsApiUrl}/people/job/attachments`, {
					jobID: job.id,
					files: [
						...responses.map(({ response, id }) => [
							id,
							{
								url: response.url,
								headers: response.headers,
								uploadedBy: profile.id,
								date: Date.now(),
							},
						]),
					],
				});
				const err: any = findErrors(jobResponse);
				if (!_.isEmpty(err)) {
					dispatch(appActions.alert('jobs:file upload:fail', 'warning'));
					return false;
				}
				dispatch(appActions.alert('jobs:file upload:success', 'success'));

				return jobResponse.data;
			} catch (error) {
				console.error('Error in jobUpdate:', error);
				return false;
			}
		};

		setTotalProgress((prev: number) => 0);

		/* eslint-disable no-await-in-loop */
		// eslint-disable-next-line no-restricted-syntax
		for (const [index, file] of files.entries()) {
			setProgressData(0, file.name);
			const initiateResponse = await initiateUpload(file);
			if (initiateResponse?.droppoint && initiateResponse.fileId) {
				// if (index % 2 === 0 && index < 5) {
				// 	onError(file.name);
				// } else {
				// await uploadFileToDroppoint(file, {});
				// }
				const uploadResponse = await uploadFileToDroppoint(file, initiateResponse.droppoint);
				if (uploadResponse) {
					const finalizeResponse = await finalizeUpload(
						file,
						initiateResponse.droppoint,
						initiateResponse.fileId,
					);
					if (finalizeResponse) {
						fileResponses.push({
							id: initiateResponse.fileId,
							response: finalizeResponse.downloadLink.info,
						});
						setCollabSessionState(initiateResponse.fileId);
					}
				}
			}
			setTotalProgress(() => Math.round((100 / files.length) * (index + 1)));
		}
		await jobUpdate(fileResponses);
		/* eslint-enable no-await-in-loop */
		onSuccess();
		dispatch(getSelectedJob(job.id));
	};

export const rejectJob =
	(jobID: string, reason: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.put(`${jobsApiUrl}/people/job/action`, {
				jobID,
				action: 'reject',
				data: reason,
			});
			onSuccess();
			dispatch({ type: 'RESET_SELECTED_JOB_SUCCESS' });
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:reject:fail'));
			onError();
		}
	};
export const acceptJob =
	(jobID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.put(`${jobsApiUrl}/people/job/action`, {
				jobID,
				action: 'accept',
			});
			console.log('job', jobID, 'accepted');
			dispatch({ type: 'ACCEPT_JOB_SUCCESS' });
			onSuccess();
			console.log('ACCEPT_JOB_SUCCESS dispatched');
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:accept:fail'));
			onError();
		}
	};

export const claimJob =
	(jobID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.put(`${jobsApiUrl}/people/job/action`, {
				jobID,
				action: 'claim',
			});
			console.log('job', jobID, 'claimed');
			dispatch({ type: 'ACCEPT_JOB_SUCCESS' });
			dispatch(getSelectedJob(jobID));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:claim:fail'));
			onError();
		}
	};

const postJobHistory = async (
	jobID: string,
	eventType: JobHistory['event']['type'],
	profileId: Profile['id'],
	region: Profile['awsRegion'],
) => {
	const jobsApiUrl = getNodeTestingAPI(region);
	try {
		await axios.post(`${jobsApiUrl}/people/job/history`, {
			jobID,
			source: {
				type: 'user',
				id: profileId,
			},
			event: {
				type: eventType,
			},
		});
	} catch (error) {
		console.error('Error in postJobHistory:', error);
		// Don't throw - we don't want history logging failures to break the main operation
	}
};

export const approveNodeSubmit =
	(
		// send to Positive transition
		jobID: string,
		nodeNamespace: string,
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const nodeUrl = getApproveNodesUrl(profile?.awsRegion, nodeNamespace);
		const tenantId = getSelectedLicenseGroupId(getState());
		const payload: any = {
			jobID,
			tenantId,
		};
		try {
			await axios.post(`${nodeUrl}/api/node/approveJob`, payload);

			await postJobHistory(jobID, 'approve', profile.id, profile.awsRegion);

			onSuccess();
			dispatch({ type: 'RESET_SELECTED_JOB_SUCCESS' });
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:submit:fail'));
			onError();
		}
	};

export const approveNodeReject =
	(
		// send to Negative transition
		jobID: string,
		nodeNamespace: string,
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const nodeUrl = getApproveNodesUrl(profile?.awsRegion, nodeNamespace);
		const tenantId = getSelectedLicenseGroupId(getState());
		const payload: any = {
			jobID,
			tenantId,
		};
		try {
			await axios.post(`${nodeUrl}/api/node/rejectJob`, payload);

			await postJobHistory(jobID, 'deny', profile.id, profile.awsRegion);

			onSuccess();
			dispatch({ type: 'RESET_SELECTED_JOB_SUCCESS' });
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:submit:fail'));
			onError();
		}
	};

export const submitJob =
	(
		jobID: string,
		allNextSteps: JobItemDetails['nextSteps'],
		selectedNextStep: string | null,
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const payload: any = {
			jobID,
			action: 'done',
		};
		if (selectedNextStep) payload.data = allNextSteps.find((s) => s.transition === selectedNextStep);
		console.log('job', jobID, 'submitted', payload);
		try {
			await axios.put(`${jobsApiUrl}/people/job/action`, payload);
			onSuccess();
			dispatch({ type: 'RESET_SELECTED_JOB_SUCCESS' });
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:submit:fail'));
			onError();
		}
	};

export const reassignJob =
	(jobID: string, recipientUserID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const payload: any = {
			jobID,
			action: 'reassign',
			data: recipientUserID,
		};
		console.log(`job ${jobID} reassigned`, payload);
		try {
			await axios.put(`${jobsApiUrl}/people/job/action`, payload);
			onSuccess();
			dispatch({ type: 'RESET_SELECTED_JOB_SUCCESS' });
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:submit:fail'));
			onError();
		}
	};

export const getJobTracker =
	(onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			const response = await axios.get(`${jobsApiUrl}/people/tracker`, {
				params: {
					userID: profile.id,
				},
			});
			console.log('tracked jobs', response.data.jobs);
			dispatch({
				type: 'GET_TRACKER_SUCCESS',
				payload: groupByID(response.data.jobs),
			});
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'tracker:get:fail'));
			onError();
		}
	};

export const trackJob =
	(jobID: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const trackedJobs = getTrackedJobs(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.post(`${jobsApiUrl}/people/tracker`, {
				jobs: [...Object.keys(trackedJobs), jobID],
			});
			console.log('tracking job', jobID);
			dispatch(getJobTracker());
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:track:fail'));
			onError();
		}
	};

export const untrackJob =
	(jobID: string): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const trackedJobs = getTrackedJobs(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		try {
			await axios.post(`${jobsApiUrl}/people/tracker`, {
				jobs: Object.keys(trackedJobs).filter((j) => j !== jobID),
			});
			console.log('untracking job', jobID);
			dispatch(getJobTracker());
		} catch (error) {
			dispatch(appActions.handleError(error, 'job:untrack:fail'));
		}
	};

export const createNewQueue =
	(name: JobQueue['name'], groupId: UserGroup['id'], users: User['id'][], active: boolean): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const body = {
			name,
			active,
			groupID: groupId,
			members: users,
		};
		try {
			await axios.post(`${jobsApiUrl}/people/queue`, body);
			console.log('creating new queue', body);
			dispatch(getTenantQueues());
		} catch (error) {
			dispatch(appActions.handleError(error, 'queue:create:fail'));
		}
	};

export const updateQueue =
	(
		queueId: JobQueue['id'],
		name: JobQueue['name'],
		groupId: UserGroup['id'],
		users: User['id'][],
		active: boolean,
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const body = {
			tenantID: tenantId,
			queueID: queueId,
			name,
			active,
			groupID: groupId,
			members: users,
		};
		try {
			await axios.put(`${jobsApiUrl}/people/queue`, body);
			console.log(`updated queue ${queueId}`, body);
			dispatch(getTenantQueues());
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'queue:update:fail'));
			onError();
		}
	};

export const removeQueue =
	(queueId: JobQueue['id']): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const params = {
			tenantID: tenantId,
			queueID: queueId,
		};
		try {
			await axios.delete(`${jobsApiUrl}/people/queue`, { params });
			console.log('removed queue', queueId);
			dispatch(getTenantQueues());
		} catch (error) {
			dispatch(appActions.handleError(error, 'queue:delete:fail'));
		}
	};
export const removeUserFromQueues =
	(userId: User['id']): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const params = {
			tenantID: tenantId,
			userID: userId,
		};
		try {
			await axios.delete(`${jobsApiUrl}/people/queues/user`, { params });
			console.log('removed from queues user', userId);
		} catch (error) {
			dispatch(appActions.handleError(error, 'queue:user:delete:fail'));
		}
	};

export const startCollab =
	(
		job: JobItemDetails,
		{
			userIds,
			guests,
			internalPermissions,
			externalPermissions,
			collabDuration,
			message,
			readReceipt,
			maxExtensionDuration,
		}: {
			userIds: User['id'][];
			guests: string[];
			internalPermissions: CollaboratorPermissions;
			externalPermissions: ExternalCollabPermissions;
			collabDuration: number;
			message: string;
			readReceipt: boolean;
			maxExtensionDuration: number;
		},
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		let guestUsers = [];
		try {
			const response = await axios.post(`/api/user/create/guest-user`, {
				tenantId: profile.tenantId,
				emails: guests,
				guestFeature: 'collaboration',
			});

			guestUsers = response.data.users;
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:start:fail'));
		}

		const formatExpire = Number(collabDuration ?? 7) * 1000 * 60 * 60 * 24;
		const expireDate = new Date(Date.now() + formatExpire).getTime();
		try {
			await axios.put(`${jobsApiUrl}/people/job`, {
				jobID: job.id,
				jobCollaborators: {
					internalUsersList: userIds?.map((item) => ({ id: item })),
					externalUsersList: guestUsers?.map((item: any) => ({ id: item.id })),
				},
				collabExpire: expireDate,
			});
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:start:fail'));
		}

		try {
			const collabRecordBody = {
				tenantId: profile.tenantId,
				jobId: job.id,
				ownerId: profile.id,
				userIds,
				guests: guestUsers?.map((item: any) => item.id),
				permissions: {
					internalCollabPermissions: internalPermissions,
					externalCollabPermissions: externalPermissions,
				},
				expireDate,
				message,
				readReceipt,
				jobName: job.name,
				maxExtension: maxExtensionDuration * 1000 * 60 * 60 * 24,
			};
			await axios.post(`${collabServUrl}/api/jobs/create/job`, collabRecordBody, {
				validateStatus: (status) => status < 400,
			});

			const request = {
				tenantId: profile.tenantId,
				jobId: job.id,
				workflowId: job.workflow.id,
				workflowName: job.workflow.name,
				userName: `${profile.firstName} ${profile.lastName}`.trim(),
			};
			await axios.post(`${collabServUrl}/api/jobs/send-invitation`, request, {
				validateStatus: (status) => status < 400,
			});

			dispatch(appActions.alert('collab:start:success', 'success'));
			dispatch(getSelectedJob(job.id));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:start:fail'));
			onError();
		}
	};

export const shareCollab =
	(
		job: JobItemDetails,
		{
			userIds,
			guests,
			message,
			expireDate,
		}: {
			userIds: User['id'][];
			guests: string[];
			message: string;
			expireDate: number;
		},
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const jobsApiUrl = getNodeTestingAPI(profile?.awsRegion);
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		let guestUsers = [];
		try {
			const response = await axios.post(`/api/user/create/guest-user`, {
				tenantId: profile.tenantId,
				emails: guests,
				guestFeature: 'collaboration',
			});

			guestUsers = response.data.users;
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:start:fail'));
		}

		try {
			await axios.put(`${jobsApiUrl}/people/job`, {
				jobID: job.id,
				jobCollaborators: {
					internalUsersList: [
						...(job?.jobCollaborators?.internalUsersList ?? []),
						...userIds.map((item) => ({ id: item })),
					],
					externalUsersList: [
						...(job?.jobCollaborators?.externalUsersList ?? []),
						...guestUsers.map((item: any) => ({ id: item.id })),
					],
				},
				collabExpire: expireDate,
			});
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:share:fail'));
		}

		try {
			const collabRecordBody = {
				tenantId: profile.tenantId,
				jobId: job.id,
				requestUser: profile.id,
				userIds,
				guests: guestUsers.map((item: any) => item.id),
				workflowId: job.workflow.id,
				workflowName: job.workflow.name,
				message,
				userName: `${profile.firstName} ${profile.lastName}`.trim(),
			};
			await axios.patch(`${collabServUrl}/api/jobs/add/collaborators`, collabRecordBody, {
				validateStatus: (status) => status < 400,
			});

			dispatch(appActions.alert('collab:start:success', 'success'));
			dispatch(getSelectedJob(job.id));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:start:fail'));
			onError();
		}
	};

export const requestCollabExtension =
	(
		job: JobItemDetails,
		{
			extensionDuration,
			message,
		}: {
			extensionDuration: number;
			message: string;
		},
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		const formatExpire = Number(extensionDuration) * 1000 * 60 * 60 * 24;

		try {
			const collabRecordBody = {
				tenantId: profile.tenantId,
				jobId: job.id,
				userId: profile.id,
				extension: formatExpire,
				message,
			};
			await axios.post(`${collabServUrl}/api/jobs/request-extension`, collabRecordBody, {
				validateStatus: (status) => status < 400,
			});

			dispatch(appActions.alert('collab:request extension:success', 'success'));
			dispatch(getSelectedJob(job.id));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:request extension:fail'));
			onError();
		}
	};

export const submitOwnerCollaborationExtensionResponse =
	(
		jobId: string | undefined,
		extensionId: number | undefined,
		message: string | undefined,
		status: 'approved' | 'rejected',
		onSuccess = () => {},
		onError = () => {},
	): AppThunk =>
	async (dispatch, getState) => {
		if (!jobId) return onError();
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		try {
			await axios.patch(
				`${collabServUrl}/api/jobs/extend`,
				{
					tenantId,
					jobId,
					extensionId,
					message,
					status,
				},
				{
					validateStatus: (stratus) => stratus < 400,
				},
			);

			dispatch(appActions.alert(`collab:request response${status ? ` ${status}` : ''}:success`, 'success'));
			onSuccess();
		} catch (error) {
			dispatch(appActions.handleError(error, 'collab:request response:fail'));
			onError();
		}
	};

export const sendReadReceipt =
	(jobId: string): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		try {
			await axios.patch(
				`${collabServUrl}/api/jobs/send-read-receipt`,
				{
					tenantId,
					jobId,
					userId: profile.id,
				},
				{
					validateStatus: (stratus) => stratus < 400,
				},
			);
		} catch (error) {
			console.log(error);
		}
	};

export const getExtensionRequestStatus =
	(jobId: string, extensionId: string, onSuccess = (extensionStatus: string) => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		try {
			const response = await axios.get(
				`${collabServUrl}/api/jobs/extension-status/${jobId}/extension/${extensionId}`,
				{
					validateStatus: (stratus) => stratus < 400,
				},
			);

			onSuccess(response.data);
		} catch (error) {
			onError();
		}
	};

export const changeCollabOwner =
	(jobId: string, recipientUserId: string, onSuccess = () => {}, onError = () => {}): AppThunk =>
	async (dispatch, getState) => {
		const profile = getProfile(getState());
		const tenantId = getSelectedLicenseGroupId(getState());
		const collabServUrl = getCollabServiceUrl(profile?.awsRegion);
		try {
			const response = await axios.patch(
				`${collabServUrl}/api/jobs/change-owner`,
				{
					tenantId,
					jobId,
					userId: recipientUserId,
				},
				{
					validateStatus: (stratus) => stratus < 400,
				},
			);
			onSuccess();
		} catch (error) {
			onError();
		}
	};
